import { getDateFromNric, isBelowAnb } from '@/utils/applicant';
import { isValid } from 'date-fns';
import { ObjectSchema, object, string } from 'yup';
import { ErrorMessage, Relationship } from '../enums';
import { QuotationFormValues } from '../types/form';

const nameField = string()
  .trim()
  .defined()
  .required(ErrorMessage.Required)
  .min(3, ErrorMessage.Name)
  .max(150, ErrorMessage.Name);

const emailField = string()
  .trim()
  .email(ErrorMessage.Email)
  .required(ErrorMessage.Required)
  .min(5, ErrorMessage.Email)
  .max(100, ErrorMessage.Email);

const occupationField = string().trim().required(ErrorMessage.Required);

export const quotationSchema: ObjectSchema<QuotationFormValues> = object({
  life_insured: object({
    name: nameField.test(
      'duplicate-name',
      ErrorMessage.DuplicatedName,
      function (value) {
        const policyOwnerName = this?.from?.[1]?.value?.policy_owner?.name;
        if (!value || !policyOwnerName) return true;
        return value !== policyOwnerName;
      },
    ),
    nric: string()
      .trim()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]{12}$/, ErrorMessage.NRIC)
      .test('valid-nric', ErrorMessage.NRIC, (value) => {
        if (!value) return true;
        return isValid(getDateFromNric(value));
      })
      .test('valid-state-code', ErrorMessage.StateCode, (value) => {
        if (!value) return true;
        const nricStateCode = value.slice(6, 8);
        return nricStateCode !== '00';
      })
      .test('valid-last-4-digits', ErrorMessage.Last4Digits, (value) => {
        if (!value) return true;
        const nricLast4Digits = value.slice(8, 12);
        return nricLast4Digits !== '0000';
      })
      .test('valid-kid', ErrorMessage.isBelow, function (value) {
        const relationship = this?.parent?.relationship; // access relationship field
        if (!value) return true;
        if (
          relationship !== Relationship.Son &&
          relationship !== Relationship.Daughter
        ) {
          return true;
        }
        return isBelowAnb(value);
      }),
    email: emailField,
    occupation: occupationField,
  })
    .defined()
    .required(ErrorMessage.Required),

  policy_owner: object({
    name: nameField.test(
      'duplicate-name',
      ErrorMessage.DuplicatedName,
      function (value) {
        const lifeInsuredName = this?.from?.[1]?.value?.life_insured?.name;
        if (!value || !lifeInsuredName) return true;
        return value !== lifeInsuredName;
      },
    ),
    nric: string()
      .trim()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]{12}$/, ErrorMessage.NRIC)
      .test('valid-nric', ErrorMessage.NRIC, (value) => {
        if (!value) return true;
        return isValid(getDateFromNric(value));
      })
      .test('valid-state-code', ErrorMessage.StateCode, (value) => {
        if (!value) return true;
        const nricStateCode = value.slice(6, 8);
        return nricStateCode !== '00';
      })
      .test('valid-last-4-digits', ErrorMessage.Last4Digits, (value) => {
        if (!value) return true;
        const nricLast4Digits = value.slice(8, 12);
        return nricLast4Digits !== '0000';
      }),
    email: emailField,
    occupation: occupationField,
  })
    .transform((value, originalValue) => {
      if (
        originalValue === null ||
        (typeof originalValue === 'object' &&
          Object.keys(originalValue).length === 0)
      ) {
        return undefined; // skip validation
      }
      return value;
    })
    .nullable()
    .notRequired()
    .default(null),
});
